// IncomeEntry.js
import React, { useState } from 'react';
import axios from 'axios';
import { MONTHS } from './constants';

function IncomeEntry({ onSubmitSuccess }) {
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Auto-generate month based on current date
    const currentDate = new Date();
    const month = MONTHS[currentDate.getMonth()];

    try {
      // Use consistent URL handling - the axios interceptor will add the base URL
      const response = await axios.post('/api/income', {
        date: month,
        amount: parseFloat(amount), // Ensure amount is a number
        source,
      });
      console.log('Income submitted:', response.data);
      setSuccess('Income added successfully!');
      setAmount('');
      setSource('');
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (err) {
      console.error('Error submitting income:', err.message);
      setError(`Failed to add income: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 flex justify-center items-center">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <header className="mb-8 glass glass-hover rounded-2xl p-6 text-center animate-slideInLeft">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 glass rounded-xl flex items-center justify-center mr-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Add Income</h1>
          </div>
          <p className="text-white/80">Track and record your income sources</p>
        </header>

        <div className="glass glass-hover rounded-2xl overflow-hidden animate-fadeIn">
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 rounded-xl glass transition-all duration-300 animate-fadeIn border-green-400/30 text-green-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">{success}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-xl glass transition-all duration-300 animate-fadeIn border-red-400/30 text-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Amount (₪)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-white/60 font-medium">₪</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="glass-input w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                    placeholder="e.g., 20000.00"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Source</label>
                <input
                  type="text"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="glass-input w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                  placeholder="e.g., Guru"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading || !amount || !source}
                className="glass-button w-full py-4 px-6 text-white font-semibold rounded-xl transition-all duration-300 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding Income...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Income
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        
        {/* Info Card */}
        <div className="glass glass-hover rounded-2xl p-6 border-l-4 border-white/30">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Income Entry Tips
          </h3>
          <ul className="space-y-3 text-white/80 text-sm">
            <li className="flex items-start">
              <svg className="h-5 w-5 text-white/60 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Income will be added to the current month ({`${MONTHS[new Date().getMonth()]} ${new Date().getFullYear()}`})</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-white/60 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Use the full amount before any deductions</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-white/60 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Include all sources of income for accurate tracking</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default IncomeEntry;